import { z } from 'zod';
import { stampedBody } from '../common/row-version.js';
import { PaginationSchema } from '../common/pagination.js';
import { PeriodParamSchema, PeriodZoneSchema, zoneRequiredWith } from '../common/period.js';
import { csv, queryBool } from '../common/query.js';
import { ClassifyDecisionSchema, DerivativeKindSchema, FLOW_PAGE_SIZE, MEDIA_GRAINS, FileClassSchema, FileFolderSchema, FileTopicSchema, FileTypeFilterSchema, MoveRoadSchema, NOTE_MAX_CHARS, PREVIEW_LINK_BATCH_MAX, } from './vocabulary.js';
// Request schemas for the files library (ADR-068). The upload is announce → do →
// confirm: POST announces (a pending row under the anchor's gate + the files verb),
// PUT streams the bytes as a raw `application/octet-stream` body (the one-file
// convention — no multipart), the commit is the PUT's success. Derivatives are
// their own small PUTs against the same pending/committed id. The reads are ONE
// question asked five ways (the shelf · the flow · the collections · the bin ·
// the options), every one scoped to an anchor.
const Id16 = z.string().min(1).max(16);
/** The anchor a file is born on. One kind per class (vocabulary CLASS_ANCHOR);
 *  the route validates the pair. Captures (ticket) are not announceable until the
 *  tickets module lands. */
export const FileAnchorSchema = z
    .object({
    // 'ticket' is admitted by the SHAPE but never by the web door: the ticket
    // class is not announceable through the API (the capture ingest is the one
    // writer, service-side) — the announce's class gate refuses it first.
    kind: z.enum(['site', 'asset', 'model', 'user', 'project', 'ticket']),
    id: Id16,
})
    .strict();
/** `<kind>:<id>` on the wire (a query param) — the reads' scope. `ticket` is a
 *  READ scope only (the hub's peek lists a ticket's media): the anchor's own
 *  files, gated by the ticket's visibility (participation, or the read key with
 *  the location in scope — the tickets module's one shared point check). */
export const ANCHOR_PARAM = /^(site|asset|model|user|project|ticket):[A-Za-z0-9_-]{1,16}$/;
export const AnchorParamSchema = z.string().max(24).regex(ANCHOR_PARAM, 'anchor must be <kind>:<id>');
export function parseAnchor(param) {
    const [kind, id] = param.split(':');
    return { kind, id };
}
// ── POST /api/projects/:projectId/files — announce ──────────────────────────
export const AnnounceFileRequestSchema = z
    .object({
    class: FileClassSchema,
    anchor: FileAnchorSchema,
    /** A document's folder (the anchor's vocabulary); absent = a described thing
     *  (media). The sniffed family decides at commit: a document family with no
     *  folder is refused (FILE_FOLDER_REQUIRED); a photo with a folder is a document. */
    folder: FileFolderSchema.nullable().optional(),
    original_name: z.string().trim().min(1).max(255),
    /** The declared size — verified at commit; the streaming cap is the class's. */
    size_bytes: z.number().int().positive(),
    /** The uploader's declared type — a HINT for the extension only; the sniffer decides. */
    declared_type: z.string().max(127).optional(),
    /** A document's display name / a capture's caption. */
    display_name: z.string().trim().min(1).max(255).optional(),
    /** Media: what it shows — the assets it names (a person's pick: trusted) … */
    subjects: z.array(Id16).max(20).optional(),
    /** … and the topics no named asset already implies. */
    topics: z.array(FileTopicSchema).max(20).optional(),
    /** Replace with a new version: the current file this one supersedes (same
     *  anchor + folder; the display name is kept; the family stitched at commit). */
    replaces_file_id: Id16.optional(),
    /** Producer idempotency (the bot's `(ticket, file_unique_id)`); unique per row. */
    ingest_key: z.string().trim().min(1).max(120).optional(),
})
    .strict();
// ── PUT …/files/:fileId/content — the bytes (raw body) ──────────────────────
// No JSON body; the route reads the stream. Nothing to declare.
// ── PUT …/files/:fileId/derivatives/:kind — a producer-made size (raw body) ──
export const DerivativeParamsSchema = z
    .object({ projectId: Id16, fileId: Id16, kind: DerivativeKindSchema })
    .strict();
// ── the reads — ONE question, asked five ways ───────────────────────────────
/** The capture's fix — the files' position filter: on site · away · none. */
export const FILE_POSITION_FILTERS = ['on', 'away', 'none'];
/** The dimensions every file answers (Person · Created · File type · Asset) plus the
 *  two nature-bound ones (Document folder · Media topic — they exclude each other)
 *  and the GPS fix (media). `q` is the shared search contract. */
export const FilesQuestionSchema = z.object({
    anchor: AnchorParamSchema,
    q: z.string().trim().max(200).optional(),
    /** Document folder — shows that folder's documents alone. */
    folder: FileFolderSchema.optional(),
    /** Media topic(s) — `none` = not described. */
    topic: csv(z.string().min(1).max(32)),
    /** Asset(s) named — `none` = no asset. */
    asset: csv(z.string().min(1).max(16)),
    /** The capture's fix: on site · away · none. */
    position: z.enum(FILE_POSITION_FILTERS).optional(),
    /** Uploader(s). */
    person: csv(Id16),
    /** The Created filter (ADR-082 §5): a period word or a day pair on `captured_at` — when the photo was taken or the
     *  document came in — the days cut in the screen's zone `tz`, which must come with it. */
    created: PeriodParamSchema.optional(),
    tz: PeriodZoneSchema,
    type: csv(FileTypeFilterSchema),
});
// Every read of the question ends with the period's refinement: a period without its zone is refused.
const createdWithZone = zoneRequiredWith(['created']);
/** GET …/files/shelf — the anchor's documents (whole) + the hub's lanes. */
export const ShelfQuerySchema = FilesQuestionSchema.strict().superRefine(createdWithZone);
export const MediaGrainSchema = z.enum(MEDIA_GRAINS);
/** GET …/files/media — the flow: an OFFSET page (FLOW_PAGE_SIZE a page under the pager — the list-read kit, ADR-078),
 *  the inbox lens, the sort, and — `group_by` — the dividers: every day / month / year band of the filtered library
 *  with its TRUE count, the page's rows stamped with theirs. The days are cut in the screen's zone `tz`, which must
 *  come with a grain as with a period. */
export const MediaQuerySchema = FilesQuestionSchema.extend({
    review: queryBool,
    sort: z.enum(['newest', 'oldest']).default('newest'),
    limit: z.coerce.number().int().positive().max(100).default(FLOW_PAGE_SIZE),
    offset: PaginationSchema.shape.offset,
    group_by: MediaGrainSchema.optional(),
})
    .strict()
    .superRefine(zoneRequiredWith(['created', 'group_by']));
/** GET …/files/collections — per subject: the count + the newest file over the WHOLE question. */
export const CollectionsQuerySchema = FilesQuestionSchema.extend({
    by: z.enum(['asset', 'topic']),
    review: queryBool,
    sort: z.enum(['newest', 'oldest']).default('newest'),
})
    .strict()
    .superRefine(createdWithZone);
/** GET …/files/bin · GET …/files/options — the anchor alone. */
export const AnchorOnlyQuerySchema = z.object({ anchor: AnchorParamSchema }).strict();
// ── POST …/files/:fileId/preview-link (one) · POST …/files/preview-links (a batch) ─
export const PreviewLinkRequestSchema = z
    .object({ kind: z.enum(['original', 'thumb', 'web']).default('original') })
    .strict();
export const PreviewLinksRequestSchema = z
    .object({
    items: z
        .array(z.object({ file_id: Id16, kind: z.enum(['original', 'thumb', 'web']).default('thumb') }).strict())
        .min(1)
        .max(PREVIEW_LINK_BATCH_MAX),
})
    .strict();
// ── PATCH …/files/:fileId — the file's own facts (the display name: a document's
// name, a web photo's caption) ──────────────────────────────────────────────
export const UpdateFileRequestSchema = z
    .object({ ...stampedBody, display_name: z.string().trim().min(1).max(255).nullable() })
    .strict();
// ── POST …/files/:fileId/classification · POST …/files/classification (bulk) ─
// The decision: confirm (the open proposal stands — `assets` names the confirmed
// ones), change (the answer replaces the guess — the rejected proposals are kept
// and marked), nothing (nothing to classify — every open proposal dismissed).
export const ClassifyFileRequestSchema = z
    .object({
    decision: ClassifyDecisionSchema,
    assets: z.array(Id16).max(20).default([]),
    topics: z.array(FileTopicSchema).max(20).default([]),
})
    .strict();
export const ClassifyFilesRequestSchema = ClassifyFileRequestSchema.extend({
    file_ids: z.array(Id16).min(1).max(100),
}).strict();
// ── POST …/files/:fileId/move — ONE smart verb: the link row re-pointed ──────
export const MoveFileRequestSchema = z
    .object({
    road: MoveRoadSchema,
    /** Another site (road = site), or the site an asset belongs to (road = asset, elsewhere). */
    site_id: Id16.nullable().optional(),
    /** Under an asset (road = asset, or road = site with an asset of that site). */
    asset_id: Id16.nullable().optional(),
    /** A document's folder in the destination's vocabulary; null for media. */
    folder: FileFolderSchema.nullable().optional(),
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.road === 'site' && !v.site_id)
        ctx.addIssue({ code: 'custom', path: ['site_id'], message: 'site required' });
    if (v.road === 'asset' && !v.asset_id)
        ctx.addIssue({ code: 'custom', path: ['asset_id'], message: 'asset required' });
});
// ── notes ───────────────────────────────────────────────────────────────────
export const NoteRequestSchema = z.object({ body: z.string().trim().min(1).max(NOTE_MAX_CHARS) }).strict();
